import {CONDITION_OPERATORS, IConditionFieldPreset} from "../../../policy-types";
import {
    ICheckboxesTreeItem
} from "../../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store";
import {makeAutoObservable} from "mobx";
import LocationTreeConverter from "./location-tree-converter";
import LocationLabelsGrouper, {ILocationLabelsGroups} from "./location-labels-grouper";
import {ICompareValueStore} from "../compare-value-factory";
import React from "react";
import LocationCompareValueList
    from "../../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/condition-field/compare-value-type-fields/location-compare-value-list/location-compare-value-list";
import {
    ICompareValueTypeComponentProps
} from "../../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/condition-field/condition-field";
import {isSet} from "../../../../../models/model-types-methods";
import {rootContainer, TYPE} from "../../../../../services/container/root";
import ErrorReportingService from "../../../../../services/error-reporting.service";

/**
 * Represents a location with country (required) and optional province/city.
 * Supports four variants:
 * - {country} - country only
 * - {country, province} - country with province
 * - {country, city} - country with city (no province)
 * - {country, province, city} - full location hierarchy
 */
export interface ILocationToken {
    country: string;
    province?: string;
    city?: string;
}

/**
 * Location comparison value model (version 2).
 * 
 * list semantics:
 * - undefined → no selection (new condition, invalid for saving)
 * - [] → select all (compressed mode, invalid for saving)
 * - [tokens] → specific selection (valid for saving)
 */
export interface ILocationCompareValueList {
    _ver: 2;
    list?: ILocationToken[];
}

/**
 * LocationCompareValueStoreList - orchestrates location comparison state.
 * Manages model/changes/uncommitted buffers and delegates to LocationTreeConverter and LocationLabelsGrouper.
 */
class LocationCompareValueStoreList implements ICompareValueStore<ILocationCompareValueList> {

    private readonly _model: ILocationCompareValueList;
    private readonly _locationsListConverterUtil: LocationTreeConverter;
    private readonly _locationLabelsGrouperUtil: LocationLabelsGrouper;

    private _changes: Partial<ILocationCompareValueList> = {};
    private _uncommittedChanges: Partial<ILocationCompareValueList> = {};

    /** Initializes store with field preset and optional compare value from DB. */
    constructor(private _fieldPreset: IConditionFieldPreset, compareValue?: string) {
        makeAutoObservable(this, {}, {autoBind: true});
        this._model = this._deserializeModel(compareValue);
        this._locationsListConverterUtil = new LocationTreeConverter(this._fieldPreset);
        this._locationLabelsGrouperUtil = new LocationLabelsGrouper();
    }

    private get errorReportingService(): ErrorReportingService {
        return rootContainer.get<ErrorReportingService>(TYPE.ErrorReportingService);
    }

    /** Handles location selection changes from UI. Extracts tokens and stores as uncommitted changes. */
    public onLocationsChanged = (items: ICheckboxesTreeItem[] | undefined) => {
        const tokens = this._locationsListConverterUtil.extractTokensFromTree(items);
        this._uncommittedChanges = {_ver: 2, list: tokens};
    }

    readonly Component: React.FC<ICompareValueTypeComponentProps> = LocationCompareValueList;

    /** Commits uncommitted UI state into committed changes. Clears if equals model. */
    commitChanges(): void {
        const commit: Partial<ILocationCompareValueList> = {
            ...this._changes,
            ...this._uncommittedChanges
        };

        /** No changes to commit */
        if (Object.keys(commit).length === 0) {

            /**
             * Could happen if commitChanges was triggered for a logical operator,
             * in this case there will be no uncommitedChanges or changes in the list itself.
             */
            return;
        }

        /** Commit equals model - no actual change */
        if (this._areCompareListsEqual(commit, this._model)) {
            this._changes = {};
            this._uncommittedChanges = {};
            return;
        }

        /** Store committed changes */
        this._changes = commit;
        this._uncommittedChanges = {};
    }

    /** Discards uncommitted UI state. */
    discardUncommittedChanges(): void {
        this._uncommittedChanges = {};
    }

    /** Returns merged snapshot: model + committed changes + uncommitted changes. */
    getUncommittedChanges(): ILocationCompareValueList {
        return {
            ...this._model,
            ...this._changes,
            ...this._uncommittedChanges
        };
    }

    /** No-op for location comparison. */
    onOperatorChanged(op: CONDITION_OPERATORS): void {
    }

    /** Sets committed changes from external payload (e.g. undo/redo). Clears if equals model. */
    setChanges(changes: string | undefined): void {
        if (!changes) {
            this._changes = {};
            return;
        }

        this._changes = this._deserializeModel(changes);

        /**
         * If an external value equals the committed model – clear changes to avoid false positives.
         */
        if (this._areCompareListsEqual(this._changes as ILocationCompareValueList, this._model)) {
            this._changes = {};
            return;
        }
    }

    /** Discards committed changes. */
    discardChanges(): void {
        this._changes = {};
    }

    /** Returns serialized model to save to DB. Uses committed changes if present. */
    getUpdatedModel(): string | undefined {
        return JSON.stringify(this.hasChanges() ? this._changes : this._model);
    }

    /** Checks if there are any committed changes. */
    hasChanges(): boolean {
        return Object.keys(this._changes).length > 0;
    }

    /** Deserializes compareValue from DB to version 2 format */
    private _deserializeModel(compareValue?: string): ILocationCompareValueList {
        if (!isSet(compareValue)) {
            return {_ver: 2, list: undefined};
        }

        let parsed;

        try {
            parsed = JSON.parse(compareValue);
        }
        catch (error) {
            this.errorReportingService.captureException(
                error as Error,
                { compareValue, component: 'LocationCompareValueStoreList' }
            );
            console.error('[LocationCompareValueStoreList] Not valid json parsed. Error:', compareValue, error);
            return {_ver: 2, list: undefined};
        }

        /** Version 2 format */
        if (parsed._ver === 2) {
            return {
                _ver: 2,
                list: parsed.list ? this._normalizeTokens(parsed.list) : undefined
            };
        }

        /** Legacy format - migrate to version 2 */
        return {_ver: 2, list: [this._createToken(parsed)]};
    }

    /** Validates and normalizes tokens array, filtering invalid entries */
    private _normalizeTokens(tokens: any[]): ILocationToken[] {
        return tokens
            .filter(token => token?.country)
            .map(token => this._createToken(token));
    }

    /** Creates validated token from raw data */
    private _createToken(raw: any): ILocationToken {
        const token: ILocationToken = {country: raw.country};

        if (raw.province) {
            token.province = raw.province;
        }

        if (raw.city) {
            token.city = raw.city;
        }

        return token;
    }

    /**
     * Compares lists by token set equality (order-independent).
     * Critical: undefined and [] are different states (no selection vs select all).
     */
    private _areCompareListsEqual(updated: Partial<ILocationCompareValueList>, model: ILocationCompareValueList): boolean {
        if (updated.list === undefined && model.list === undefined) {
            return true;
        }

        if (updated.list === undefined || model.list === undefined) {
            return false;
        }

        const updatedTokenSet = new Set<string>(updated.list.map((token) => JSON.stringify(token)));
        const modelTokenSet = new Set<string>(model.list.map((token) => JSON.stringify(token)));

        if (updatedTokenSet.size !== modelTokenSet.size) {
            return false;
        }

        for (const key of updatedTokenSet) {
            if (!modelTokenSet.has(key)) {
                return false;
            }
        }
        return true;
    }

    /** Returns full options tree for dropdown. */
    public getOptionsTree(): ICheckboxesTreeItem[] {
        return this._locationsListConverterUtil.getOptionsTree();
    }

    /** Returns selected value tree for dropdown. Computed on-demand from current state. */
    public getValueTree(): ICheckboxesTreeItem[] | undefined {
        return this._locationsListConverterUtil.getValueTree(this.getUncommittedChanges().list);
    }

    /**
     * Returns grouped labels for rendering tags.
     * Enriches tokens (codes → names) via LocationTreeConverter,
     * then groups via LocationLabelsGrouper.
     */
    public getGroupedLabels(): ILocationLabelsGroups {
        const humanReadableTokens: ILocationToken[] | undefined = this.getUncommittedChanges().list?.map((token): ILocationToken => ({
            country: this._locationsListConverterUtil.getCountryName(token.country),
            province: token.province ? this._locationsListConverterUtil.getProvinceName(token.country, token.province) : undefined,
            city: token.city ? this._locationsListConverterUtil.getCityName(token.country, token.city) : undefined
        }));
        return this._locationLabelsGrouperUtil.getGroupedLabels(humanReadableTokens);
    }
}

export default LocationCompareValueStoreList;


